import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { getConfig } from '@/config/config.ts';
import { mapToOrderTile } from '@/domain/FxOrder/FxOrder.utils.ts';
import { summonOrderOrdersReducer } from '@/store/blotter/workflows/summonOrder/extraReducers.ts';
import { espStreamSubscribed } from '@/store/esp/slice.ts';
import {
  submitAlgoFulfilledExtraReducer,
  submitAlgoPendingExtraReducer,
  submitAlgoRejectedExtraReducer,
  validateAlgoFulfilledExtraReducer,
  validateAlgoPendingExtraReducer,
  validateAlgoRejectedExtraReducer,
} from '@/store/orders/algoOrders/extraReducers.ts';
import {
  submitFulfilled as submitCallOrderFulfilledExtraReducer,
  submitPending as submitCallOrderPendingExtraReducer,
  submitRejected as submitCallOrderRejectedExtraReducer,
} from '@/store/orders/call/extraReducers/submit.ts';
import {
  validateFulfilled as validateCallOrderFulfilledExtraReducer,
  validatePending as validateCallOrderPendingExtraReducer,
  validateRejected as validateCallOrderRejectedExtraReducer,
} from '@/store/orders/call/extraReducers/validate.ts';
import {
  submitFixingFulfilledExtraReducer,
  submitFixingPendingExtraReducer,
  submitFixingRejectedExtraReducer,
  validateFixingFulfilledExtraReducer,
  validateFixingPendingExtraReducer,
  validateFixingRejectedExtraReducer,
} from '@/store/orders/fixing/extraReducers.ts';
import { isOrderType, ORDER_GENERATOR_BY_TYPE } from '@/store/orders/helpers.ts';
import { ALL_ORDER_TYPES, type OrderById } from '@/store/orders/state.ts';
import {
  submitStopLossFulfilledExtraReducer,
  submitStopLossPendingExtraReducer,
  submitStopLossRejectedExtraReducer,
  validateStopLossFulfilledExtraReducer,
  validateStopLossPendingExtraReducer,
  validateStopLossRejectedExtraReducer,
} from '@/store/orders/stopLoss/extraReducers.ts';
import {
  submitTakeProfitFulfilledExtraReducer,
  submitTakeProfitPendingExtraReducer,
  submitTakeProfitRejectedExtraReducer,
  validateTakeProfitFulfilledExtraReducer,
  validateTakeProfitPendingExtraReducer,
  validateTakeProfitRejectedExtraReducer,
} from '@/store/orders/takeProfitOrder/extraReducers.ts';
import { reduceOrderOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { addTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';

export const ordersSlice = createSlice({
  name: 'orders',
  reducerPath: 'orders',

  initialState: {} as OrderById,

  reducers: {
    resetOrder: (
      state,
      action: PayloadAction<{
        tileId: string;
        userPreferences: UserPreferencesState;
      }>,
    ) => {
      const tileId = action.payload.tileId;

      if (isNotDefined(state[tileId])) {
        console.error(`[resetOrder] Tile with id ${tileId} not found in state.`);
        return;
      }

      // Reset the tile to its initial state
      state[tileId] = ORDER_GENERATOR_BY_TYPE[state[tileId].type]();
      state[tileId].properties.notificationEmails = action.payload.userPreferences.splitNotifications
        ? action.payload.userPreferences.splitNotificationsEmailsOrders
        : action.payload.userPreferences.emails;
    },
    changeOrderMode: (state, action: PayloadAction<{ tileId: string; mode: 'EDIT' | 'VIEW' }>) => {
      const { tileId, mode } = action.payload;

      if (isNotDefined(state[tileId])) {
        console.error(`[changeOrderMode] Tile with id ${tileId} not found in state.`);
        return;
      }

      state[tileId].properties.mode = mode;
    },
    orderFailed: (state, action: PayloadAction<{ tileId: string; errorMessage: string }>) => {
      const { tileId, errorMessage } = action.payload;

      if (isNotDefined(state[tileId])) {
        console.error(`[orderFailed] Tile with id ${tileId} not found in state.`);
        return;
      }

      state[tileId].properties.status = { type: 'failed', errorMessage };
    },
    changeClientId: (state, action: PayloadAction<{ tileId: string; clientId: number | undefined }>) => {
      const { tileId, clientId } = action.payload;

      if (isNotDefined(state[tileId])) {
        console.error(`[changeClientId] Tile with id ${tileId} not found in state.`);
        return;
      }

      state[tileId].clientId = clientId;
    },
  },

  extraReducers: (builder) => {
    //  ██████╗ ██████╗ ██╗██████╗     ████████╗██╗██╗     ███████╗
    // ██╔════╝ ██╔══██╗██║██╔══██╗    ╚══██╔══╝██║██║     ██╔════╝
    // ██║  ███╗██████╔╝██║██║  ██║       ██║   ██║██║     █████╗
    // ██║   ██║██╔══██╗██║██║  ██║       ██║   ██║██║     ██╔══╝
    // ╚██████╔╝██║  ██║██║██████╔╝       ██║   ██║███████╗███████╗
    //  ╚═════╝ ╚═╝  ╚═╝╚═╝╚═════╝        ╚═╝   ╚═╝╚══════╝╚══════╝

    builder.addCase(
      addTile,
      (
        state,
        {
          payload: {
            tile: { tileId, productType },
            userPreferences,
          },
        },
      ) => {
        if (getConfig().app.environment === 'devci') {
          console.warn('TODO: user userPreferences to init tile properties', userPreferences);
        }

        // already defined
        if (isDefined(state[tileId])) {
          return state;
        }

        // not for a cash
        if (!isOrderType(productType)) {
          return state;
        }

        // ProductType === Swap alors on crée une nouvelle tile
        return {
          ...state,
          [tileId]: ORDER_GENERATOR_BY_TYPE[productType](),
        };
      },
    );

    builder.addCase(
      updateTileProductType,
      (state, { payload: { tileId, productType, userPreferences, currencyPair } }) => {
        if (getConfig().app.environment === 'devci') {
          console.warn('TODO: user userPreferences to init tile properties', userPreferences);
        }

        // Si tileId existe dans state, est que productType ne designe pas un Order alors on supprime la tile
        if (isDefined(state[tileId]) && !isOrderType(productType)) {
          delete state[tileId];
          return;
        }

        if (isOrderType(productType)) {
          state[tileId] = ORDER_GENERATOR_BY_TYPE[productType]();
          state[tileId].properties.notificationEmails = userPreferences.splitNotifications
            ? userPreferences.splitNotificationsEmailsOrders
            : userPreferences.emails;

          if (isDefined(currencyPair)) {
            const [currency1, currency2] = currencyPair.split('/');
            if (isDefined(currency1) && isDefined(currency2)) {
              state[tileId].properties.currencyPair = {
                currency1,
                currency2,
              };
            }
          }
        }
      },
    );

    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return;
      }

      delete state[tileId];
    });

    // ███████╗███████╗██████╗
    // ██╔════╝██╔════╝██╔══██╗
    // █████╗  ███████╗██████╔╝
    // ██╔══╝  ╚════██║██╔═══╝
    // ███████╗███████║██║
    // ╚══════╝╚══════╝╚═╝

    builder.addCase(espStreamSubscribed, (state, action) => {
      const { tileId, tiering, currencyPair } = action.payload;

      const tile = state[tileId];

      if (isNotDefined(tile)) {
        return;
      }

      const [currency1, currency2] = currencyPair.split('/');

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[ordersSlice / espStreamSubscribed] Invalid currency pair: ${currencyPair}`);
        return;
      }

      if (
        isNotDefined(tile.properties.currencyPair) ||
        tile.properties.currencyPair.currency1 !== currency1 ||
        tile.properties.currencyPair.currency2 !== currency2
      ) {
        console.warn(
          `[ordersSlice / espStreamSubscribed] Currency pair mismatch for tile ${tileId}. Expected: ${tile.properties.currencyPair?.currency1}/${tile.properties.currencyPair?.currency2}, Received: ${currency1}/${currency2}`,
        );
        return;
      }

      if (!ALL_ORDER_TYPES.includes(tile.type)) {
        console.error(
          `[ordersSlice / espStreamSubscribed] Tile with id ${tileId} is not a TakeProfitOrder or StopLossOrder or FixingOrder or Twap+Order.`,
        );
        return;
      }

      tile.properties.currencyPair = {
        currency1,
        currency2,
        tiering,
      };
    });

    // ███████╗██╗  ██╗████████╗██████╗  █████╗     ██████╗ ███████╗██████╗ ██╗   ██╗ ██████╗███████╗██████╗ ███████╗
    // ██╔════╝╚██╗██╔╝╚══██╔══╝██╔══██╗██╔══██╗    ██╔══██╗██╔════╝██╔══██╗██║   ██║██╔════╝██╔════╝██╔══██╗██╔════╝
    // █████╗   ╚███╔╝    ██║   ██████╔╝███████║    ██████╔╝█████╗  ██║  ██║██║   ██║██║     █████╗  ██████╔╝███████╗
    // ██╔══╝   ██╔██╗    ██║   ██╔══██╗██╔══██║    ██╔══██╗██╔══╝  ██║  ██║██║   ██║██║     ██╔══╝  ██╔══██╗╚════██║
    // ███████╗██╔╝ ██╗   ██║   ██║  ██║██║  ██║    ██║  ██║███████╗██████╔╝╚██████╔╝╚██████╗███████╗██║  ██║███████║
    // ╚══════╝╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝    ╚═╝  ╚═╝╚══════╝╚═════╝  ╚═════╝  ╚═════╝╚══════╝╚═╝  ╚═╝╚══════╝

    // Order summoning from blotter
    summonOrderOrdersReducer(builder);

    // take profit
    validateTakeProfitFulfilledExtraReducer(builder);
    validateTakeProfitPendingExtraReducer(builder);
    validateTakeProfitRejectedExtraReducer(builder);

    submitTakeProfitFulfilledExtraReducer(builder);
    submitTakeProfitRejectedExtraReducer(builder);
    submitTakeProfitPendingExtraReducer(builder);

    // stop loss
    validateStopLossFulfilledExtraReducer(builder);
    validateStopLossPendingExtraReducer(builder);
    validateStopLossRejectedExtraReducer(builder);

    submitStopLossPendingExtraReducer(builder);
    submitStopLossFulfilledExtraReducer(builder);
    submitStopLossRejectedExtraReducer(builder);

    // fixing
    validateFixingFulfilledExtraReducer(builder);
    validateFixingPendingExtraReducer(builder);
    validateFixingRejectedExtraReducer(builder);

    submitFixingPendingExtraReducer(builder);
    submitFixingFulfilledExtraReducer(builder);
    submitFixingRejectedExtraReducer(builder);

    // algo
    validateAlgoFulfilledExtraReducer(builder);
    validateAlgoPendingExtraReducer(builder);
    validateAlgoRejectedExtraReducer(builder);

    submitAlgoPendingExtraReducer(builder);
    submitAlgoFulfilledExtraReducer(builder);
    submitAlgoRejectedExtraReducer(builder);

    // call order
    validateCallOrderPendingExtraReducer(builder);
    validateCallOrderFulfilledExtraReducer(builder);
    validateCallOrderRejectedExtraReducer(builder);

    submitCallOrderPendingExtraReducer(builder);
    submitCallOrderFulfilledExtraReducer(builder);
    submitCallOrderRejectedExtraReducer(builder);

    // ██╗    ██╗ ██████╗ ██████╗ ██╗  ██╗███████╗██████╗  █████╗  ██████╗███████╗
    // ██║    ██║██╔═══██╗██╔══██╗██║ ██╔╝██╔════╝██╔══██╗██╔══██╗██╔════╝██╔════╝
    // ██║ █╗ ██║██║   ██║██████╔╝█████╔╝ ███████╗██████╔╝███████║██║     █████╗
    // ██║███╗██║██║   ██║██╔══██╗██╔═██╗ ╚════██║██╔═══╝ ██╔══██║██║     ██╔══╝
    // ╚███╔███╔╝╚██████╔╝██║  ██║██║  ██╗███████║██║     ██║  ██║╚██████╗███████╗
    //  ╚══╝╚══╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝  ╚═╝ ╚═════╝╚══════╝

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      // clear all products created before workspace has loaded
      Object.keys(state).forEach((id) => {
        delete state[id];
      });

      (action.payload.products.order ?? []).forEach((orderProduct) => {
        //TODO #1102: we should remove this after the fix is in prod ! needed temporarily because users have in session(db) tiles with order products that are not in 'creating' status
        //Those products can't be loaded in workspace, so we ignore them during workspace loading to avoid breaking users workspace
        // if in a year you still see this comment just remove it with the condition :D
        if (orderProduct.properties.status.type !== 'creating') {
          return;
        }

        state[orderProduct.tileId] = mapToOrderTile(orderProduct);
      });
    });
    reduceOrderOnFetchSharedWorkspace(builder);
  },
  selectors: {
    getAllOrdersProducts: (state) =>
      Object.entries(state).map(([tileId, product]) => ({
        tileId,
        ...product,
      })),
    orderTileExists: (state, orderId: string | undefined) =>
      isDefined(orderId) && Object.entries(state).some(([, order]) => order.id === orderId),
  },
});
